import { RoutePath } from "../../../features/routes"
import { CodedDataType, TableName } from "../../../tables"
import { deliveryOrderHelpers, DeliveryStatus, useDeliveryOrderLoaders } from "../../deliveries"
import { useLotLoaders, useProductHooks, useProductLoaders } from "../../products"
import { usePurchasesHooks } from "../../purchases"
import { useStockLoaders } from "../../stock"
import { useOperationLoaders } from "../loaders/operationLoaders"
import { Item } from "../state/types"
import { ItemValidation } from "./types"
import { usePricesHooks } from "./pricesHooks"

export const useOperationHooks = () => {
    const operationLoaders = useOperationLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const productLoaders = useProductLoaders()
    const stockLoaders = useStockLoaders()
    const lotLoaders = useLotLoaders()
    const { describeQuantity } = useProductHooks()
    // Habilitado individualmente o por categoría (Configuración de productos → Venta Sin Stock)
    const { canSellWithoutStock } = usePurchasesHooks()
    const pricesHooks = usePricesHooks()

    const calculateDeliveryStatus = async (
        table: TableName,
        operationId: string
    ) => {
        const { loadOperation } = operationLoaders()
        const operation = await loadOperation(table, operationId, true)
        const isFullyEmitted = operation!.items.every(item => item.deliveryStats.pending === 0)
        const hasEmittedItems = operation!.items.some(item => item.deliveryStats.emitted > 0)

        const { loadOperationDeliveryOrders } = deliveryOrderLoaders()
        // Read from the db: the cached state is the snapshot of the last render, so it doesn't include the updates just saved
        const deliveryOrders = await loadOperationDeliveryOrders(operationId, false)
        // Annulled orders without any movement don't count for the operation status
        const relevantOrders = deliveryOrders.filter(deliveryOrder => !deliveryOrder.cancelled || deliveryOrder.items.some(item => {
            const { dispatched, delivered, returned = 0 } = item.deliveryStats
            return dispatched + delivered + returned > 0
        }))
        if (!hasEmittedItems && relevantOrders.length === 0) {
            return DeliveryStatus.PENDING
        }

        const isFullyDelivered = relevantOrders.every(deliveryOrder => deliveryOrder.items.every(item => (item.deliveryStats.pending + item.deliveryStats.dispatched) === 0))

        return isFullyEmitted && isFullyDelivered ? DeliveryStatus.DELIVERED : DeliveryStatus.PARTIALLY_DELIVERED
    }

    const validateItems = async (
        items: Partial<Item>[],
        itemValidations: ItemValidation[],
        branchId?: string
    ): Promise<string[]> => {
        const validations: string[] = []
        const validateNotEmpty = itemValidations.includes(ItemValidation.NOT_EMPTY)
        const validateDefinedLot = itemValidations.includes(ItemValidation.DEFINED_LOT)
        const validateAvailableStock = itemValidations.includes(ItemValidation.AVAILABLE_STOCK)
        const validateReservedStock = itemValidations.includes(ItemValidation.RESERVED_STOCK)
        const validateForSale = itemValidations.includes(ItemValidation.FOR_SALE)

        if (validateNotEmpty && items.length === 0) {
            validations.push('Debe agregar al menos 1 producto')
        }

        await items.reduce(async (previousPromise, item) => {
            await previousPromise

            if (item.quantity === 0) return

            const { loadProduct } = productLoaders()
            const product = (await loadProduct(item.productId!))!

            // Items that don't come from the product selector (e.g. copied from a budget) can bring a product without price
            if (validateForSale && !pricesHooks.product.isForSale(product.id)) {
                validations.push(`El producto ${product.name} no tiene precio: Compras tiene que cargar su precio de compra`)
            } else if (validateDefinedLot && product.loted && !item.lotId) {
                validations.push(`Debe seleccionar una partida para el producto ${product.name}`)
            } else if (validateAvailableStock || validateReservedStock) {
                const { loadProductStocks } = stockLoaders()
                const productStocks = await loadProductStocks(product.id, branchId!, item.lotId)
                const availableUnits = productStocks.reduce((acc, stock) => acc + stock.availableUnits, 0)
                const reservedUnits = productStocks.reduce((acc, stock) => acc + stock.reservedUnits, 0)
                const units = validateAvailableStock ? availableUnits : reservedUnits

                // Productos habilitados para vender sin stock: no es error, se advierte con getNegativeStockWarnings
                if (validateAvailableStock && canSellWithoutStock(product)) return

                const { loadLot } = lotLoaders()
                const lot = item.lotId ? await loadLot(item.lotId) : undefined
                const productLabel = `el producto ${product.name}${lot ? ' (' + lot.code + ')' : ''}`

                if (units < item.quantity!) {
                    validations.push(units === 0 ?
                        `Debe eliminar o modificar la partida d${productLabel} porque no contiene stock${validateReservedStock ? ' reservado' : ''}` :
                        `Debe ingresar una cantidad menor o igual al stock${validateReservedStock ? ' reservado' : ''} (${units}) para ${productLabel}`
                    )
                }
            }
        }, Promise.resolve())

        return validations
    }

    // Advertencias para productos habilitados para vender sin stock cuyo stock disponible no alcanza
    const getNegativeStockWarnings = async (
        items: Partial<Item>[],
        branchId: string
    ): Promise<string[]> => {
        const warnings: string[] = []
        const { loadProduct } = productLoaders()
        const { loadProductStocks } = stockLoaders()

        await items.reduce(async (previousPromise, item) => {
            await previousPromise
            if (!item.productId || !item.quantity) return

            const product = (await loadProduct(item.productId))!
            if (!canSellWithoutStock(product)) return

            const productStocks = await loadProductStocks(product.id, branchId)
            const availableUnits = productStocks.reduce((acc, stock) => acc + stock.availableUnits, 0)
            if (availableUnits < item.quantity) {
                warnings.push(`${product.name}: ${describeQuantity(Math.max(availableUnits, 0), product)} disponibles, se quieren usar ${describeQuantity(item.quantity, product)}`)
            }
        }, Promise.resolve())

        return warnings
    }

    return {
        calculateDeliveryStatus,
        validateItems,
        getNegativeStockWarnings
    }
}

export const operationHelpers = {
    describeCode: (operation: CodedDataType) => `#${operation.code}`,

    describeNoun: (table: TableName, plural = false): string => {
        return new Map<TableName, string>()
            .set(TableName.PURCHASE_ORDERS, plural ? 'Compras' : 'Compra')
            .set(TableName.SALE_ORDERS, plural ? 'Ventas' : 'Venta')
            .set(TableName.HOARD_ORDERS, plural ? 'Acopios' : 'Acopio')
            .set(TableName.BUDGETS, plural ? 'Presupuestos' : 'Presupuesto')
            .get(table) || (plural ? 'Operaciones' : 'Operación')
    },

    getRoutePath: (table: TableName): RoutePath => {
        return new Map<TableName, RoutePath>()
            .set(TableName.PURCHASE_ORDERS, RoutePath.PURCHASES)
            .set(TableName.SALE_ORDERS, RoutePath.SALES)
            .set(TableName.HOARD_ORDERS, RoutePath.HOARDS)
            .get(table)!
    }
}
